import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_KEYWORD_CATEGORIES, MOCK_MOVES } from '../data/mockData';
import api, { setAccessToken } from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock user presets
const USER_PRESETS = {
  beginner: {
    name: 'Beginner Preset',
    description: 'Perfect for those starting their breaking journey',
    variations: {
      basics: ['Spin', 'Jump', 'Slide', 'Freeze', 'Repeat', 'Otherside', 'Reversed'],
      flow: ['Step', 'Rock', 'Bounce', 'Down'],
      energy: ['Slow', 'Energy', 'Transfer']
    },
    favoriteTypes: ['toprock', 'footwork']
  },
  intermediate: {
    name: 'Intermediate Preset', 
    description: 'For b-boys/b-girls developing their style',
    variations: {
      position: ['Front', 'Back', 'Top', 'Bottom', 'Left', 'Right'],
      basics: ['Spin', 'Jump', 'Slide', 'Freeze', 'Repeated', 'Doubled', 'Kicked', 'Otherside', 'Reversed'
      ],
      'hip hop': ['Roll', 'Wave', 'Bounce', 'Pop', 'Rock', 'Groove'],
      space: ['Step', 'Longer', 'Higher', 'Down', 'Circled', 'Slowed', 'Energized', 'Transferred', 'Switched'],
      original: ['Isolated', 'Threaded', 'Grabbed foot', 'Touched foot w/ hand', 'Spidered', 'Hooked', 'Drill'],      
    },
    favoriteTypes: ['toprock', 'footwork', 'powermoves', 'freezes', 'transitions', 'flips']
  },
  advanced: {
    name: 'Advanced Preset',
    description: 'Full arsenal for experienced breakers',
    variations: { ...DEFAULT_KEYWORD_CATEGORIES },
    favoriteTypes: ['toprock', 'footwork', 'powermoves', 'freezes', 'transitions', 'flips']
  },
  custom: {
    name: 'Custom Setup',
    description: 'Start with a clean slate and build your own',
    variations: {
      'my style': []
    },
    favoriteTypes: []
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false); // Track if preferences are loaded
  const [arsenal, setArsenal] = useState([]); // moves maîtrisés (ids)
  const [learning, setLearning] = useState([]); // moves à apprendre (ids)
  const [learningProgress, setLearningProgress] = useState({}); // tracking des apprentissages en cours
  const [combos, setCombos] = useState([]); // combos créés par l'utilisateur (loaded from API)
  const [comboRevisions, setComboRevisions] = useState([]); // combos en révision
  // Crews & routines (stockage global simulé)
  const [crews, setCrews] = useState([]);
  const [crewMemberships, setCrewMemberships] = useState([]);
  const [crewShares, setCrewShares] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activeCrewId, setActiveCrewId] = useState(() => localStorage.getItem('crewmate_active_crew_id') || null);
  const [folders, setFolders] = useState({
    'TopRock': {
      name: 'TopRock',
      type: 'folder',
      children: {
        'Basic Steps': { name: 'Basic Steps', type: 'subfolder', children: {} },
        'Advanced Steps': { name: 'Advanced Steps', type: 'subfolder', children: {} }
      }
    },
    'Footwork': {
      name: 'Footwork',
      type: 'folder',
      children: {
        'Basic Footwork': { name: 'Basic Footwork', type: 'subfolder', children: {} },
        'Advanced Footwork': { name: 'Advanced Footwork', type: 'subfolder', children: {} }
      }
    },
    'PowerMoves': {
      name: 'PowerMoves',
      type: 'folder',
      children: {
        'Basic Power': { name: 'Basic Power', type: 'subfolder', children: {} },
        'Advanced Power': { name: 'Advanced Power', type: 'subfolder', children: {} }
      }
    },
    'Freezes': {
      name: 'Freezes',
      type: 'folder',
      children: {
        'Basic Freezes': { name: 'Basic Freezes', type: 'subfolder', children: {} },
        'Advanced Freezes': { name: 'Advanced Freezes', type: 'subfolder', children: {} }
      }
    },
    'Transitions': {
      name: 'Transitions',
      type: 'folder',
      children: {
        'TopRock to Footwork': { name: 'TopRock to Footwork', type: 'subfolder', children: {} },
        'Footwork to Power': { name: 'Footwork to Power', type: 'subfolder', children: {} }
      }
    },
    'Flips': {
      name: 'Flips',
      type: 'folder',
      children: {
        'Basic Flips': { name: 'Basic Flips', type: 'subfolder', children: {} },
        'Advanced Flips': { name: 'Advanced Flips', type: 'subfolder', children: {} }
      }
    },
    'Combos': {
      name: 'Combos',
      type: 'folder',
      children: {
        'Technical Combos': { name: 'Technical Combos', type: 'subfolder', children: {} },
        'Ending Combos': { name: 'Ending Combos', type: 'subfolder', children: {} },
        'Battle Combos': { name: 'Battle Combos', type: 'subfolder', children: {} }
      }
    }
  }); // dossiers et sous-dossiers

  // Mock users database
  const [users, setUsers] = useState(() => {
    const savedUsers = localStorage.getItem('crewmate_users');
    if (savedUsers) {
      return JSON.parse(savedUsers);
    }
    return [
      {
        id: '1',
        username: 'demo_user',
        email: 'demo@crewmate.com',
        password: 'demo123',
        preset: 'intermediate',
        customVariations: USER_PRESETS.intermediate.variations,
        moves: MOCK_MOVES,
        profile: {
          displayName: 'Demo User',
          crewName: 'Mock Crew',
          joinedDate: '2024-01-01'
        }
      }
    ];
  });

  useEffect(() => {
    // Check for saved user session
    const savedUser = localStorage.getItem('crewmate_user');
    const savedToken = localStorage.getItem('crewmate_token');
    
    // Check if token is expired
    if (savedToken) {
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        const isExpired = payload.exp * 1000 < Date.now();
        
        if (isExpired) {
          console.log('🔒 Stored token is expired, clearing session...');
          localStorage.removeItem('crewmate_user');
          localStorage.removeItem('crewmate_token');
          setUser(null);
          setAccessToken(null);
          setIsLoading(false);
          return;
        }
        
        setAccessToken(savedToken);
      } catch (e) {
        console.log('🔒 Invalid token format, clearing session...');
        localStorage.removeItem('crewmate_user');
        localStorage.removeItem('crewmate_token');
        setUser(null);
        setAccessToken(null);
        setIsLoading(false);
        return;
      }
    }
    
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
    }
    
    setIsLoading(false);
  }, []);

  // After login, load preferences from server
  useEffect(() => {
    console.log('🔍 useEffect triggered - user:', user, 'user?.id:', user?.id);
    
    const loadPrefs = async () => {
      try {
        console.log('🔄 Loading preferences from server for user:', user?.id);
        console.log('🔄 Current token:', localStorage.getItem('crewmate_token')?.slice(0, 20) + '...');
        console.log('🔄 API call to /me/preferences...');
        
        console.log('🔄 Making API request...');
        const response = await api.get('/me/preferences');
        console.log('🔄 API response received:', response);
        const { data } = response;
          console.log('✅ Preferences loaded:', data);

          setArsenal(data.arsenal || []);
          setLearning(data.learning || []);
          setLearningProgress(data.learningProgress || {});
          setFolders(data.folders || {});
          setComboRevisions(data.comboRevisions || []);
          const incomingCrews = data.crews || [];
          const incomingMemberships = data.crewMemberships || [];
          console.log('🔄 Incoming crews from API:', incomingCrews);
          console.log('🔄 Incoming memberships from API:', incomingMemberships);

          // Ensure owner has a membership entry for each owned crew
          const missingOwnerMemberships = (incomingCrews || [])
            .filter(c => c && c.ownerUserId === (user?.id || ''))
            .filter(c => !(incomingMemberships || []).some(m => m.crewId === c.id && m.userId === (user?.id || '')))
            .map(c => ({ crewId: c.id, userId: user.id, role: 'owner', joinedAt: new Date().toISOString() }));

          const combinedMemberships = [...incomingMemberships, ...missingOwnerMemberships];
          console.log('🔄 Missing owner memberships:', missingOwnerMemberships);
          console.log('🔄 Combined memberships:', combinedMemberships);

          setCrews(incomingCrews);
          setCrewMemberships(combinedMemberships);
          setRoutines(data.routines || []);
          setCrewShares(data.crewShares || []);

          // Mettre à jour le profil utilisateur depuis les préférences si présent
          if (data.profile) {
            setUser(prev => prev ? { ...prev, profile: data.profile } : null);
          }
          // Charger les invitations après préférences (pour être sûr d'avoir l'email/username à jour)
          try {
            const invRes = await api.get('/invitations');
            setInvitations(invRes.data || []);
          } catch (e) {}

          console.log('🎯 State updated - Arsenal:', data.arsenal, 'Learning:', data.learning);
          console.log('🎯 State updated - Crews:', incomingCrews, 'Routines:', data.routines);
          console.log('🎯 State updated - CrewMemberships:', combinedMemberships);

        // Ensure folders exist; if empty, initialize with defaults and persist
        const serverFolders = data.folders || {};
        if (!serverFolders || Object.keys(serverFolders).length === 0) {
          console.log('📁 Initializing default folders...');
          const defaultFolders = {
            'TopRock': {
              name: 'TopRock',
              type: 'folder',
              children: {
                'Basic Steps': { name: 'Basic Steps', type: 'subfolder', children: {} },
                'Advanced Steps': { name: 'Advanced Steps', type: 'subfolder', children: {} }
              }
            },
            'Footwork': {
              name: 'Footwork',
              type: 'folder',
              children: {
                'Basic Footwork': { name: 'Basic Footwork', type: 'subfolder', children: {} },
                'Advanced Footwork': { name: 'Advanced Footwork', type: 'subfolder', children: {} }
              }
            },
            'PowerMoves': {
              name: 'PowerMoves',
              type: 'folder',
              children: {
                'Basic Power': { name: 'Basic Power', type: 'subfolder', children: {} },
                'Advanced Power': { name: 'Advanced Power', type: 'subfolder', children: {} }
              }
            },
            'Freezes': {
              name: 'Freezes',
              type: 'folder',
              children: {
                'Basic Freezes': { name: 'Basic Freezes', type: 'subfolder', children: {} },
                'Advanced Freezes': { name: 'Advanced Freezes', type: 'subfolder', children: {} }
              }
            },
            'Transitions': {
              name: 'Transitions',
              type: 'folder',
              children: {
                'TopRock to Footwork': { name: 'TopRock to Footwork', type: 'subfolder', children: {} },
                'Footwork to Power': { name: 'Footwork to Power', type: 'subfolder', children: {} }
              }
            },
            'Flips': {
              name: 'Flips',
              type: 'folder',
              children: {
                'Basic Flips': { name: 'Basic Flips', type: 'subfolder', children: {} },
                'Advanced Flips': { name: 'Advanced Flips', type: 'subfolder', children: {} }
              }
            },
            'Combos': {
              name: 'Combos',
              type: 'folder',
              children: {
                'Technical Combos': { name: 'Technical Combos', type: 'subfolder', children: {} },
                'Ending Combos': { name: 'Ending Combos', type: 'subfolder', children: {} },
                'Battle Combos': { name: 'Battle Combos', type: 'subfolder', children: {} }
              }
            }
          };
          setFolders(defaultFolders);
          // Persist folders asynchronously without blocking
          api.patch('/me/preferences', { folders: defaultFolders })
            .then(() => console.log('✅ Default folders persisted'))
            .catch(e => console.error('❌ Failed to persist default folders:', e));
        } else {
          setFolders(serverFolders);
        }
        // Load moves from server
        if (data.moves && data.moves.length > 0) {
          setUser(prev => prev ? { ...prev, moves: data.moves } : null);
        } else {
          // Fallback: utiliser les moves mock si pas de moves sur le serveur
          setUser(prev => prev ? { ...prev, moves: MOCK_MOVES } : null);
        }

        // Persist synthesized owner memberships if any were missing (asynchronously)
        if (missingOwnerMemberships.length > 0) {
          api.patch('/me/preferences', { crewMemberships: combinedMemberships })
            .then(() => console.log('✅ Owner memberships backfilled and persisted'))
            .catch(persistErr => console.error('❌ Failed to persist backfilled memberships:', persistErr));
        }

        setPreferencesLoaded(true);
      } catch (e) {
        console.error('❌ Failed to load preferences:', e);
        console.error('❌ Error details:', {
          message: e.message,
          status: e.response?.status,
          statusText: e.response?.statusText,
          data: e.response?.data,
          stack: e.stack
        });
        
        // If token is expired (401), logout will be handled by API interceptor
        if (e.response?.status === 401) {
          console.log('🔒 Authentication failed - token expired');
          return; // Don't use fallback data for auth errors
        }
        
        // Fallback: Use mock data for crews and memberships (only for non-auth errors)
        console.log('🔄 Using fallback data for crews...');
        const fallbackCrews = [
          {
            id: "1756914521465",
            name: "crewmate",
            description: "",
            ownerUserId: "68b82f47245271ee52454ccb",
            createdAt: "2025-09-03T15:48:41.465Z"
          }
        ];
        const fallbackMemberships = [
          {
            crewId: "1756914521465",
            userId: "68b82f47245271ee52454ccb",
            role: "owner",
            joinedAt: "2025-09-04T09:16:35.759Z"
          },
          {
            crewId: "1756914521465",
            userId: "68b95b91355f7c4a62e509e7",
            role: "member",
            joinedAt: "2025-09-04T10:19:21.687679"
          }
        ];
        
        setCrews(fallbackCrews);
        setCrewMemberships(fallbackMemberships);
        setPreferencesLoaded(true);
      }
    };
    // Only load once per session; otherwise any user state update would refetch prefs.
    if (user && !preferencesLoaded) {
      loadPrefs();
    }
  }, [user, preferencesLoaded]);

  // Sauvegarder les utilisateurs dans localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem('crewmate_users', JSON.stringify(users));
  }, [users]);

  // comboRevisions and folders are now loaded from API in loadPrefs

  // arsenal, learning, learningProgress, comboRevisions, and folders are now managed via API

  // Crews & routines are now persisted via API, no more localStorage
  // useEffect(() => {
  //   localStorage.setItem('crewmate_crews', JSON.stringify(crews));
  // }, [crews]);
  // useEffect(() => {
  //   localStorage.setItem('crewmate_crew_memberships', JSON.stringify(crewMemberships));
  // }, [crewMemberships]);
  // useEffect(() => {
  //   localStorage.setItem('crewmate_crew_shares', JSON.stringify(crewShares));
  // }, [crewShares]);
  // useEffect(() => {
  //   localStorage.setItem('crewmate_routines', JSON.stringify(routines));
  // }, [routines]);
  useEffect(() => {
    if (activeCrewId) localStorage.setItem('crewmate_active_crew_id', activeCrewId);
  }, [activeCrewId]);

  // folders are now managed via API

  // folders are now loaded from API in loadPrefs

  const addToArsenal = (moveId) => {
    console.log('🔄 Adding move to arsenal:', moveId);
    // Avoid side-effects inside setState updater (React StrictMode can invoke updater twice in dev).
    const nextArsenal = (arsenal || []).includes(moveId) ? (arsenal || []) : [...(arsenal || []), moveId];
    console.log('📦 New arsenal state:', nextArsenal);
    setArsenal(nextArsenal);
    (async () => {
      try {
        console.log('💾 Persisting arsenal to backend...');
        await api.patch('/me/preferences', { arsenal: nextArsenal });
        console.log('✅ Arsenal persisted successfully');
      } catch (e) {
        console.error('❌ Failed to persist arsenal:', e);
      }
    })();
    setLearning(prev => prev.filter(id => id !== moveId));
  };
  const addToLearning = (moveId, targetDays = null) => {
    console.log('🔄 Adding move to learning:', moveId);
    setLearning(prev => {
      if (prev.includes(moveId)) return prev;
      const newLearning = [...prev, moveId];
      console.log('📚 New learning state:', newLearning);
      
      // Initialiser learningProgress pour ce move
      setLearningProgress(prev => {
        const updated = {
          ...prev,
          [moveId]: {
            ...prev[moveId],
            startDate: new Date().toISOString(),
            targetDays: targetDays || null,
            targetDate: targetDays ? new Date(Date.now() + targetDays * 24 * 60 * 60 * 1000).toISOString() : null,
            notes: prev[moveId]?.notes || ""
          }
        };
        // Persist learningProgress
        try { 
          console.log('💾 Persisting learning and learningProgress to backend...');
          api.patch('/me/preferences', { learning: newLearning, learningProgress: updated }); 
          console.log('✅ Learning and learningProgress persisted successfully');
        } catch (e) {
          console.error('❌ Failed to persist learning:', e);
        }
        return updated;
      });
      
      return newLearning;
    });
    setArsenal(prev => prev.filter(id => id !== moveId));
  };
  const removeFromArsenal = (moveId) => {
    const next = (arsenal || []).filter(id => id !== moveId);
    setArsenal(next);
    try { api.patch('/me/preferences', { arsenal: next }); } catch {}
  };
  const removeFromLearning = (moveId) => {
    const nextLearning = (learning || []).filter(id => id !== moveId);
    setLearning(nextLearning);
    setLearningProgress(prev => {
      const updated = { ...prev };
      delete updated[moveId];
      // persist
      try { api.patch('/me/preferences', { learning: nextLearning, learningProgress: updated }); } catch {}
      return updated;
    });
  };
  const moveLearningToArsenal = (moveId) => {
    const nextLearning = (learning || []).filter(id => id !== moveId);
    const nextArsenal = (arsenal || []).includes(moveId) ? arsenal : [...(arsenal || []), moveId];
    setLearning(nextLearning);
    setArsenal(nextArsenal);
    setLearningProgress(prev => {
      const updated = { ...prev };
      delete updated[moveId];
      try { api.patch('/me/preferences', { learning: nextLearning, arsenal: nextArsenal, learningProgress: updated }); } catch {}
      return updated;
    });
  };

  // Learning Progress Tracking
  const updateLearningObjective = (moveId, targetDays, notes = "") => {
    setLearningProgress(prev => {
      const updated = {
        ...prev,
        [moveId]: {
          ...prev[moveId],
          targetDays,
          targetDate: targetDays ? new Date(Date.now() + targetDays * 24 * 60 * 60 * 1000).toISOString() : null,
          notes
        }
      };
      // Persist to server
      try { api.patch('/me/preferences', { learningProgress: updated }); } catch {}
      return updated;
    });
  };

  const updateLearningNotes = (moveId, notes) => {
    setLearningProgress(prev => {
      const updated = {
        ...prev,
        [moveId]: {
          ...prev[moveId],
          notes
        }
      };
      // Persist to server
      try { api.patch('/me/preferences', { learningProgress: updated }); } catch {}
      return updated;
    });
  };

  const getLearningStats = (moveId) => {
    const progress = learningProgress[moveId];
    if (!progress) return null;
    
    // Si startDate n'est pas défini, utiliser la date actuelle
    const startDate = progress.startDate ? new Date(progress.startDate) : new Date();
    const now = new Date();
    const daysElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    
    let status = 'on-track';
    let progress_percentage = 0;
    
    if (progress.targetDays) {
      progress_percentage = Math.min((daysElapsed / progress.targetDays) * 100, 100);
      if (daysElapsed > progress.targetDays) {
        status = 'overdue';
      } else if (daysElapsed > progress.targetDays * 0.8) {
        status = 'warning';
      }
    }
    
    return {
      daysElapsed,
      targetDays: progress.targetDays,
      status,
      progress_percentage,
      startDate: progress.startDate,
      targetDate: progress.targetDate,
      notes: progress.notes
    };
  };

  // Combo management functions (server-backed)
  const fetchCombos = async () => {
    try {
      const { data } = await api.get('/combos');
      setCombos(data);
    } catch (e) {}
  };

  useEffect(() => {
    if (user) {
      fetchCombos();
    }
  }, [user?.id]);

  // Invitations - fetch à la connexion
  useEffect(() => {
    const loadInvitations = async () => {
      try {
        const { data } = await api.get('/invitations');
        setInvitations(data || []);
      } catch (e) {}
    };
    if (user) {
      loadInvitations();
    }
  }, [user?.id]);

  const updateCombo = async (comboId, updatedCombo) => {
    const payload = { ...updatedCombo };
    const { data } = await api.patch(`/combos/${comboId}`, payload);
    // Reload combos from API to ensure tree view has latest data
    await fetchCombos();
  };

  const addCombo = async (newCombo) => {
    const { data } = await api.post('/combos', newCombo);
    // Reload combos from API to ensure tree view has latest data
    await fetchCombos();
  };

  const removeCombo = async (comboId) => {
    await api.delete(`/combos/${comboId}`);
    setCombos(prev => prev.filter(combo => combo.id !== comboId));
  };

  const getCombo = (comboId) => {
    return combos.find(combo => combo.id === comboId);
  };

  // Combo revision functions
  const addComboToRevision = async (comboId) => {
    const newComboRevisions = comboRevisions.includes(comboId) ? comboRevisions : [...comboRevisions, comboId];
    setComboRevisions(newComboRevisions);
    try {
      await api.patch('/me/combo-revisions', { comboRevisions: newComboRevisions });
    } catch (error) {
      console.error('Failed to save combo revisions:', error);
    }
  };

  const removeComboFromRevision = async (comboId) => {
    const newComboRevisions = comboRevisions.filter(id => id !== comboId);
    setComboRevisions(newComboRevisions);
    try {
      await api.patch('/me/combo-revisions', { comboRevisions: newComboRevisions });
    } catch (error) {
      console.error('Failed to save combo revisions:', error);
    }
  };

  const getComboRevisionStatus = (comboId) => {
    return comboRevisions.includes(comboId);
  };

  const updateFolders = async (newFolders) => {
    setFolders(newFolders);
    try {
      await api.patch('/me/folders', { folders: newFolders });
    } catch (error) {
      console.error('Failed to save folders:', error);
    }
  };

  // Signature sur moves/combos
  const setMoveSignature = (moveId, isSignature) => {
    if (!user) return;
    const updatedMoves = (user.moves || []).map(m => m.id === moveId ? { ...m, isSignature: !!isSignature } : m);
    updateUserMoves(updatedMoves);
  };
  const setComboSignature = async (comboId, isSignature) => {
    await updateCombo(comboId, { isSignature: !!isSignature });
  };
  const setComboStatus = async (comboId, status) => {
    await updateCombo(comboId, { status });
  };

  // Crews
  const createCrew = async ({ name, description }) => {
    if (!user) return null;
    const newCrew = {
      id: Date.now().toString(),
      name,
      description: description || '',
      ownerUserId: user.id,
      createdAt: new Date().toISOString()
    };
    
    // Update local state atomically
    const ownerMembership = { crewId: newCrew.id, userId: user.id, role: 'owner', joinedAt: new Date().toISOString() };
    const nextCrews = [...crews, newCrew];
    const nextCrewMemberships = [...crewMemberships, ownerMembership];
    setCrews(nextCrews);
    setCrewMemberships(nextCrewMemberships);
    setActiveCrewId(newCrew.id);
    
    // Persist to backend with the new state
    try {
      api.patch('/me/preferences', { 
        crews: nextCrews,
        crewMemberships: nextCrewMemberships
      }).then(() => {
        console.log('✅ Crew persisted to backend');
      }).catch(e => {
        console.error('❌ Failed to persist crew:', e);
      });
    } catch (e) {
      console.error('❌ Failed to persist crew:', e);
    }
    
    return newCrew;
  };
  const setActiveCrew = (crewId) => setActiveCrewId(crewId);
  const getMyCrews = () => {
    if (!user) return [];
    console.log('🔍 getMyCrews - user.id:', user.id);
    console.log('🔍 getMyCrews - crewMemberships:', crewMemberships);
    console.log('🔍 getMyCrews - crews:', crews);
    
    const membershipIds = (crewMemberships || []).filter(m => m.userId === user.id).map(m => m.crewId);
    const owned = (crews || []).filter(c => c.ownerUserId === user.id);
    const memberCrews = (crews || []).filter(c => membershipIds.includes(c.id));
    const map = new Map();
    [...owned, ...memberCrews].forEach(c => map.set(c.id, c));
    const result = Array.from(map.values());
    console.log('🔍 getMyCrews - myCrewIds:', membershipIds);
    console.log('🔍 getMyCrews - result:', result);
    return result;
  };
  // Version synchrone pour les cas où on a déjà les données locales
  const getCrewMembersSync = (crewId) => {
    const base = (crewMemberships || []).filter(m => m.crewId === crewId);
    const withUsers = base.map(m => {
      let resolvedUser = users.find(u => u.id === m.userId);
      if (!resolvedUser && user && user.id === m.userId) {
        resolvedUser = user;
      }
      if (!resolvedUser) {
        resolvedUser = { id: m.userId, username: `User ${m.userId.slice(-4)}`, email: 'Unknown' };
      }
      return { ...m, user: resolvedUser };
    });
    const crew = (crews || []).find(c => c.id === crewId);
    if (crew && crew.ownerUserId && !withUsers.some(m => m.userId === crew.ownerUserId)) {
      let ownerUser = users.find(u => u.id === crew.ownerUserId);
      if (!ownerUser && user && user.id === crew.ownerUserId) {
        ownerUser = user;
      }
      if (!ownerUser) {
        ownerUser = { id: crew.ownerUserId, username: `User ${crew.ownerUserId.slice(-4)}`, email: 'Unknown' };
      }
      withUsers.push({ crewId, userId: crew.ownerUserId, role: 'owner', joinedAt: crew.createdAt || new Date().toISOString(), user: ownerUser });
    }
    return withUsers;
  };

  // Version asynchrone pour récupérer les vraies données depuis l'API
  const getCrewMembers = async (crewId) => {
    try {
      console.log('🔄 getCrewMembers - Starting API call for crew:', crewId);
      
      // Ajouter un timeout pour éviter les appels bloqués
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('API call timeout after 10 seconds')), 10000)
      );
      
      // Utiliser le nouvel endpoint pour récupérer les vrais utilisateurs
      const apiPromise = api.get(`/crews/${crewId}/members`);
      const { data } = await Promise.race([apiPromise, timeoutPromise]);
      
      console.log('✅ getCrewMembers - API response received:', data);
      const result = data.map(member => ({
        crewId,
        userId: member.userId,
        role: member.role,
        joinedAt: member.joinedAt,
        user: {
          id: member.userId,
          username: member.username,
          email: member.email,
          profile: member.profile,
          arsenal: member.arsenal || [],
          moves: member.moves || []
        }
      }));
      console.log('✅ getCrewMembers - Processed result:', result);
      return result;
    } catch (e) {
      console.error('❌ getCrewMembers - Failed to fetch crew members:', e);
      console.error('❌ getCrewMembers - Error details:', e.message);
      // Fallback vers la version synchrone
      const fallback = getCrewMembersSync(crewId);
      console.log('🔄 getCrewMembers - Using fallback:', fallback);
      return fallback;
    }
  };
  const inviteUserToCrew = ({ crewId, userId, role = 'member' }) => {
    // MVP: ajouter directement comme membre
    if (crewMemberships.find(m => m.crewId === crewId && m.userId === userId)) return;
    setCrewMemberships(prev => [...prev, { crewId, userId, role, joinedAt: new Date().toISOString() }]);
  };

  const inviteUserByEmailOrUsername = async ({ crewId, emailOrUsername, role = 'member' }) => {
    try {
      const isEmail = emailOrUsername.includes('@');
      const payload = {
        crewId,
        role,
        ...(isEmail ? { invitedEmail: emailOrUsername } : { invitedUsername: emailOrUsername })
      };
      await api.post('/invitations', payload);
      return { success: true, message: `Invitation sent to ${emailOrUsername}` };
    } catch (error) {
      console.error('Error inviting user:', error);
      return { success: false, message: 'Failed to send invitation' };
    }
  };
  const updateCrewMemberRole = ({ crewId, userId, role }) => {
    setCrewMemberships(prev => prev.map(m => (m.crewId === crewId && m.userId === userId) ? { ...m, role } : m));
  };
  const removeCrewMember = ({ crewId, userId }) => {
    setCrewMemberships(prev => prev.filter(m => !(m.crewId === crewId && m.userId === userId)));
  };

  const leaveCrew = ({ crewId }) => {
    if (!user) return;
    // Ne pas permettre au propriétaire de quitter son propre crew
    const crew = crews.find(c => c.id === crewId);
    if (crew && crew.ownerUserId === user.id) {
      return { success: false, message: 'You cannot leave a crew you own. Transfer ownership first.' };
    }
    
    // Retirer l'utilisateur du crew
    setCrewMemberships(prev => prev.filter(m => !(m.crewId === crewId && m.userId === user.id)));
    
    // Si c'était le crew actif, changer vers un autre crew
    if (activeCrewId === crewId) {
      const remainingCrews = getMyCrews().filter(c => c.id !== crewId);
      if (remainingCrews.length > 0) {
        setActiveCrew(remainingCrews[0].id);
      } else {
        setActiveCrew(null);
      }
    }
    
    return { success: true, message: 'Successfully left the crew' };
  };

  // Invitations - décision
  const decideInvitation = async ({ invitationId, accept }) => {
    try {
      await api.post(`/invitations/${invitationId}/decision`, { accept: !!accept });
      // Rafraîchir les invitations
      const { data } = await api.get('/invitations');
      setInvitations(data || []);
      // Rafraîchir memberships depuis préférences (en cas d'acceptation)
      try {
        const pref = await api.get('/me/preferences');
        const incomingMemberships = pref.data.crewMemberships || [];
        setCrewMemberships(incomingMemberships);
        setCrews(pref.data.crews || crews);
      } catch (_) {}
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  };

  // Partage (un contenu -> un seul crew au max)
  const shareContentToCrew = ({ contentType, contentId, crewId }) => {
    if (!user) return;
    setCrewShares(prev => {
      // Éviter doublons exacts
      const exists = prev.find(s => s.contentType === contentType && s.contentId === contentId && s.crewId === crewId);
      const base = exists ? prev.filter(s => !(s.contentType === contentType && s.contentId === contentId && s.crewId === crewId)) : prev;
      // Construire un snapshot léger
      let snapshot = null;
      if (contentType === 'move') {
        const move = (user.moves || []).find(m => m.id === contentId);
        if (move) {
          snapshot = {
            id: move.id,
            name: move.name,
            description: move.description || '',
            type: move.type,
            isSignature: !!move.isSignature
          };
        }
      } else if (contentType === 'combo') {
        const combo = (combos || []).find(c => c.id === contentId);
        if (combo) {
          snapshot = {
            id: combo.id,
            name: combo.name,
            description: combo.description || '',
            isSignature: !!combo.isSignature,
            status: combo.status || 'draft',
            moves: (combo.moves || []).map(m => ({ id: m.id, name: m.name, type: m.type }))
          };
        }
      }
      const newShare = { id: `${contentType}-${contentId}-${crewId}`, contentType, contentId, ownerUserId: user.id, crewId, createdAt: new Date().toISOString(), snapshot };
      return [...base, newShare];
    });
  };
  const unshareContent = ({ contentType, contentId }) => {
    setCrewShares(prev => prev.filter(s => !(s.contentType === contentType && s.contentId === contentId)));
  };
  const isContentSharedToCrew = ({ contentType, contentId, crewId }) => {
    return !!crewShares.find(s => s.contentType === contentType && s.contentId === contentId && s.crewId === crewId);
  };
  const getSharedItemsForCrew = (crewId) => crewShares.filter(s => s.crewId === crewId);
  const toggleContentShare = ({ contentType, contentId, crewId }) => {
    if (isContentSharedToCrew({ contentType, contentId, crewId })) {
      setCrewShares(prev => prev.filter(s => !(s.contentType === contentType && s.contentId === contentId && s.crewId === crewId)));
    } else {
      shareContentToCrew({ contentType, contentId, crewId });
    }
  };

  // Routines
  const fetchRoutines = async () => {
    try {
      const { data } = await api.get('/routines');
      setRoutines(data);
    } catch (e) {}
  };

  useEffect(() => {
    if (user) {
      fetchRoutines();
    }
  }, [user?.id]);

  const createRoutine = async ({ crewId, name, description = '', status = 'draft' }) => {
    if (!user) return null;
    const { data } = await api.post('/routines', { crewId, name, description, status });
    setRoutines(prev => [data, ...prev]);
    return data;
  };
  const updateRoutineMeta = async (routineId, updates) => {
    const { data } = await api.patch(`/routines/${routineId}`, updates);
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };
  const deleteRoutine = async (routineId) => {
    await api.delete(`/routines/${routineId}`);
    setRoutines(prev => prev.filter(r => r.id !== routineId));
  };
  const addParticipantToRoutine = async ({ routineId, userId, roleName }) => {
    try {
      // Mettre à jour l'état local immédiatement
      const updatedRoutine = routines.find(r => r.id === routineId);
      if (!updatedRoutine) return;
      
      const newParticipants = [...updatedRoutine.participants, { userId, roleName: roleName || '' }];
      setRoutines(prev => prev.map(r => 
        r.id === routineId 
          ? { ...r, participants: newParticipants }
          : r
      ));
      
      // Persister sur le serveur
      await api.patch(`/routines/${routineId}`, { participants: newParticipants });
      console.log('✅ Participant added and persisted to server');
    } catch (e) {
      console.error('❌ Failed to add participant:', e);
      // Revert local state on error
      setRoutines(prev => prev.map(r => 
        r.id === routineId 
          ? { ...r, participants: r.participants.filter(p => p.userId !== userId) }
          : r
      ));
    }
  };
  const removeParticipantFromRoutine = async ({ routineId, userId }) => {
    try {
      // Mettre à jour l'état local immédiatement
      const updatedRoutine = routines.find(r => r.id === routineId);
      if (!updatedRoutine) return;
      
      const newParticipants = updatedRoutine.participants.filter(p => p.userId !== userId);
      setRoutines(prev => prev.map(r => 
        r.id === routineId 
          ? { ...r, participants: newParticipants }
          : r
      ));
      
      // Persister sur le serveur
      await api.patch(`/routines/${routineId}`, { participants: newParticipants });
      console.log('✅ Participant removed and persisted to server');
    } catch (e) {
      console.error('❌ Failed to remove participant:', e);
      // Revert local state on error
      setRoutines(prev => prev.map(r => 
        r.id === routineId 
          ? { ...r, participants: [...r.participants, { userId, roleName: '' }] }
          : r
      ));
    }
  };
  const addSegmentToRoutine = async ({ routineId, sourceType, sourceOwnerUserId, sourceId, sourceVersion = 1, snapshot }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const segment = {
      id: `${routineId}-${Date.now()}`,
      orderIndex: 999999,
      sourceType,
      sourceId,
      sourceOwnerUserId,
      sourceVersion,
      snapshot
    };
    const newSegments = [...routine.segments, segment].map((s, idx) => ({ ...s, orderIndex: idx }));
    const { data } = await api.patch(`/routines/${routineId}`, { segments: newSegments });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };
  const removeSegmentFromRoutine = async ({ routineId, segmentId }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const newSegments = routine.segments.filter(s => s.id !== segmentId).map((s, idx) => ({ ...s, orderIndex: idx }));
    const { data } = await api.patch(`/routines/${routineId}`, { segments: newSegments });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };
  const reorderRoutineSegments = async ({ routineId, fromIndex, toIndex }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const arr = [...routine.segments];
    const [moved] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, moved);
    const newSegments = arr.map((s, idx) => ({ ...s, orderIndex: idx }));
    const { data } = await api.patch(`/routines/${routineId}`, { segments: newSegments });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };
  const duplicateSegment = async ({ routineId, segmentId }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const idx = routine.segments.findIndex(s => s.id === segmentId);
    if (idx === -1) return;
    const original = routine.segments[idx];
    const copiedSnapshot = JSON.parse(JSON.stringify(original.snapshot || {}));
    const copy = { ...original, id: `${routineId}-${Date.now()}`, snapshot: copiedSnapshot };
    const arr = [...routine.segments];
    arr.splice(idx + 1, 0, copy);
    const newSegments = arr.map((s, i) => ({ ...s, orderIndex: i }));
    const { data } = await api.patch(`/routines/${routineId}`, { segments: newSegments });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };
  const setSegmentAssignments = async ({ routineId, segmentId, assignments }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const segs = routine.segments.map(s => s.id === segmentId ? { ...s, assignments: assignments || [] } : s);
    const { data } = await api.patch(`/routines/${routineId}`, { segments: segs });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };

  const updateSegmentVariations = async ({ routineId, segmentId, variations }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const segs = routine.segments.map(s => s.id === segmentId ? { ...s, snapshot: { ...(s.snapshot || {}), variations: [...variations] } } : s);
    const { data } = await api.patch(`/routines/${routineId}`, { segments: segs });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };

  // Mise à jour générique du snapshot d'un segment (nom, description, matrix, timeUnit, etc.)
  const updateSegmentSnapshot = async ({ routineId, segmentId, snapshotUpdates }) => {
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return;
    const segs = routine.segments.map(s => s.id === segmentId ? { ...s, snapshot: { ...(s.snapshot || {}), ...(snapshotUpdates || {}) } } : s);
    const { data } = await api.patch(`/routines/${routineId}`, { segments: segs });
    setRoutines(prev => prev.map(r => r.id === routineId ? data : r));
  };

  // Droits d'édition: créateur + admins/owner du crew
  const canEditRoutine = (routineId) => {
    if (!user) return false;
    const routine = routines.find(r => r.id === routineId);
    if (!routine) return false;
    if (routine.createdByUserId === user.id) return true;
    const membership = crewMemberships.find(m => m.crewId === routine.crewId && m.userId === user.id);
    return membership && (membership.role === 'owner' || membership.role === 'admin');
  };

  // Fonctions de réorganisation des dossiers
  const moveFolderUp = (folderKey) => {
    const folderKeys = Object.keys(folders);
    const currentIndex = folderKeys.indexOf(folderKey);
    if (currentIndex > 0) {
      const newFolders = {};
      folderKeys.forEach((key, index) => {
        if (index === currentIndex - 1) {
          newFolders[folderKey] = folders[folderKey];
        } else if (index === currentIndex) {
          newFolders[folderKeys[currentIndex - 1]] = folders[folderKeys[currentIndex - 1]];
        } else {
          newFolders[key] = folders[key];
        }
      });
      setFolders(newFolders);
    }
  };

  const moveFolderDown = (folderKey) => {
    const folderKeys = Object.keys(folders);
    const currentIndex = folderKeys.indexOf(folderKey);
    if (currentIndex < folderKeys.length - 1) {
      const newFolders = {};
      folderKeys.forEach((key, index) => {
        if (index === currentIndex) {
          newFolders[folderKeys[currentIndex + 1]] = folders[folderKeys[currentIndex + 1]];
        } else if (index === currentIndex + 1) {
          newFolders[folderKey] = folders[folderKey];
        } else {
          newFolders[key] = folders[key];
        }
      });
      setFolders(newFolders);
    }
  };

  // Fonctions de réorganisation des sous-dossiers
  const moveSubfolderUp = (folderKey, subfolderKey) => {
    const subfolderKeys = Object.keys(folders[folderKey]?.children || {});
    const currentIndex = subfolderKeys.indexOf(subfolderKey);
    if (currentIndex > 0) {
      const newFolders = { ...folders };
      const newChildren = {};
      subfolderKeys.forEach((key, index) => {
        if (index === currentIndex - 1) {
          newChildren[subfolderKey] = folders[folderKey].children[subfolderKey];
        } else if (index === currentIndex) {
          newChildren[subfolderKeys[currentIndex - 1]] = folders[folderKey].children[subfolderKeys[currentIndex - 1]];
        } else {
          newChildren[key] = folders[folderKey].children[key];
        }
      });
      newFolders[folderKey] = {
        ...newFolders[folderKey],
        children: newChildren
      };
      setFolders(newFolders);
    }
  };

  const moveSubfolderDown = (folderKey, subfolderKey) => {
    const subfolderKeys = Object.keys(folders[folderKey]?.children || {});
    const currentIndex = subfolderKeys.indexOf(subfolderKey);
    if (currentIndex < subfolderKeys.length - 1) {
      const newFolders = { ...folders };
      const newChildren = {};
      subfolderKeys.forEach((key, index) => {
        if (index === currentIndex) {
          newChildren[subfolderKeys[currentIndex + 1]] = folders[folderKey].children[subfolderKeys[currentIndex + 1]];
        } else if (index === currentIndex + 1) {
          newChildren[subfolderKey] = folders[folderKey].children[subfolderKey];
        } else {
          newChildren[key] = folders[folderKey].children[key];
        }
      });
      newFolders[folderKey] = {
        ...newFolders[folderKey],
        children: newChildren
      };
      setFolders(newFolders);
    }
  };

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { access_token } = res.data;
      setAccessToken(access_token);
      localStorage.setItem('crewmate_token', access_token);
  
      const meRes = await api.get('/me');
      const base = meRes.data;
  
      const userSession = {
        id: base.id,
        email: base.email,
        username: base.username,
        createdAt: base.createdAt,
        profile: base.profile || {
          displayName: base.username,
          crewName: '',
          joinedDate: (base.createdAt ? new Date(base.createdAt) : new Date())
            .toISOString()
            .split('T')[0],
        },
        customVariations:
          base.customVariations && Object.keys(base.customVariations).length > 0
            ? base.customVariations
            : DEFAULT_KEYWORD_CATEGORIES,
        moves:
          Array.isArray(base.moves) && base.moves.length > 0
            ? base.moves
            : [...MOCK_MOVES],
      };
  
      setUser(userSession);
      localStorage.setItem('crewmate_user', JSON.stringify(userSession));
      return { success: true };
    } catch (e) {
      const backendMessage = e.response?.data?.detail;
      const generic =
        e.code === 'ERR_NETWORK'
          ? "Erreur réseau ou CORS lors de l'appel à l'API"
          : 'Invalid credentials';
      return { success: false, error: backendMessage || generic };
    }
  };

  const register = async (userData) => {
    try {
      await api.post('/auth/register', {
        email: userData.email,
        username: userData.username,
        password: userData.password,
      });
      const loginResult = await login(userData.email, userData.password);
      return loginResult;
    } catch (e) {
      const backendMessage = e.response?.data?.detail;
      const generic =
        e.code === 'ERR_NETWORK'
          ? "Erreur réseau ou CORS lors de l'appel à l'API"
          : 'Erreur lors de la création du compte';
      return { success: false, error: backendMessage || generic };
    }
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('crewmate_user');
    localStorage.removeItem('crewmate_token');
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!user) return { success: false, error: 'User not logged in' };
    
    try {
      await api.patch('/me/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true, message: 'Mot de passe modifié avec succès' };
    } catch (e) {
      const errorMessage = e.response?.data?.detail || 'Erreur lors de la modification du mot de passe';
      return { success: false, error: errorMessage };
    }
  };

  const updateUserProfile = async (profileData) => {
    if (!user) return;
    try {
      const nextProfile = { ...user.profile };
      // Ne pas injecter email dans le profil; l'email est géré côté /me
      Object.entries(profileData || {}).forEach(([k, v]) => {
        if (k !== 'email') nextProfile[k] = v;
      });

      // 1) Mettre à jour le profil (crewName, displayName, etc.) côté préférences
      try {
        await api.patch('/me/preferences', { profile: nextProfile });
      } catch (e) {
        // no-op: on continue pour tenter l'update email si besoin
      }

      // 2) Mettre à jour l'email s'il a changé
      let updatedEmail = user.email;
      let emailUpdated = false;
      let emailError = null;
      if (profileData?.email && profileData.email !== user.email) {
        try {
          const { data: meUpdated } = await api.patch('/me', { email: profileData.email });
          updatedEmail = meUpdated.email || updatedEmail;
          emailUpdated = true;
        } catch (e) {
          // si conflit d'email, on garde l'ancien email localement
          emailError = 'Cet email est déjà utilisé ou invalide.';
        }
      }

      const updatedUser = {
        ...user,
        email: updatedEmail,
        profile: nextProfile
      };
      setUser(updatedUser);
      localStorage.setItem('crewmate_user', JSON.stringify(updatedUser));
      return { success: true, emailUpdated, emailError };
    } catch (e) {
      // fallback local minimal si quelque chose échoue
      const updatedUser = {
        ...user,
        profile: { ...user.profile, ...profileData }
      };
      setUser(updatedUser);
      localStorage.setItem('crewmate_user', JSON.stringify(updatedUser));
      return { success: false, error: 'Échec de la mise à jour du profil.' };
    }
  };

  const updateUserVariations = (variations) => {
    const updatedUser = {
      ...user,
      customVariations: variations
    };
    setUser(updatedUser);
    localStorage.setItem('crewmate_user', JSON.stringify(updatedUser));
  };

  const updateUserMoves = async (moves) => {
    try {
      await api.patch('/me/preferences', { moves });
      setUser(prev => prev ? { ...prev, moves } : null);
    } catch (e) {
      // fallback to local update
      setUser(prev => prev ? { ...prev, moves } : null);
    }
  };

  // Basics vs Signatures
  const isBasicMove = (move) => {
    // Heuristique MVP: un move sans auteur explicite (dans MOCK) est basic si isSignature n'est pas vrai
    return !move.isSignature && !move.custom; // on pourra raffiner (ex: champ move.isBasic)
  };

  // Debug: Log the context value being provided

  const value = {
    user,
    login,
    register,
    logout,
    changePassword,
    isLoading,
    preferencesLoaded, // Add this to the context
    users,
    userPresets: USER_PRESETS,
    updateUserProfile,
    updateUserVariations,
    updateUserMoves,
    // Expose state immediately; components can use preferencesLoaded to show loading UI if needed.
    arsenal,
    learning,
    learningProgress,
    combos,
    folders,
    updateFolders,
    moveFolderUp,
    moveFolderDown,
    moveSubfolderUp,
    moveSubfolderDown,
    addToArsenal,
    addToLearning,
    removeFromArsenal,
    removeFromLearning,
    moveLearningToArsenal,
    updateLearningObjective,
    updateLearningNotes,
    getLearningStats,
    addCombo,
    updateCombo,
    removeCombo,
    getCombo,
    comboRevisions,
    addComboToRevision,
    removeComboFromRevision,
    getComboRevisionStatus,
    // Signature & statut combos
    setMoveSignature,
    setComboSignature,
    setComboStatus,
    // Crews & partage
    crews,
    crewMemberships,
    crewShares,
    routines,
    activeCrewId,
    setActiveCrew,
    createCrew,
    getMyCrews,
    getCrewMembers,
    getCrewMembersSync,
    inviteUserToCrew,
    inviteUserByEmailOrUsername,
    updateCrewMemberRole,
    removeCrewMember,
    leaveCrew,
    shareContentToCrew,
    unshareContent,
    isContentSharedToCrew,
    getSharedItemsForCrew,
    toggleContentShare,
    // Invitations
    invitations,
    decideInvitation,
    createRoutine,
    updateRoutineMeta,
    deleteRoutine,
    addParticipantToRoutine,
    removeParticipantFromRoutine,
    addSegmentToRoutine,
    removeSegmentFromRoutine,
    reorderRoutineSegments,
    duplicateSegment,
    setSegmentAssignments,
    updateSegmentVariations,
    updateSegmentSnapshot,
    canEditRoutine
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};